import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, Zap } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const PLACEMENT_LABELS: Record<string, string> = {
  homepage_banner: "Homepage Banner",
  in_feed: "In-Feed Native",
  article_inline: "Article Inline",
  sidebar: "Sidebar Spotlight",
  audio_brief: "Audio Brief Sponsor",
  sector_mid: "Sector Mid Banner",
  nav_menu: "Navigation Spotlight",
  sector_inline: "Sector In-Feed",
  research_spotlight: "Research Spotlight",
  premium_insight: "Premium Insight",
};

export default function AdPricing() {
  const queryClient = useQueryClient();
  const [matrix, setMatrix] = useState<any>(null);
  const [prime, setPrime] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["admin-ad-pricing"],
    queryFn: async () => (await api.get("/admin/ad-pricing")).data,
  });

  useEffect(() => {
    if (data && !matrix) setMatrix(JSON.parse(JSON.stringify(data.placements)));
    if (data?.prime && !prime) setPrime({ ...data.prime });
  }, [data, matrix, prime]);

  const durations: number[] = data?.durations || [7, 14, 30, 60, 90];

  const setPrice = (pk: string, d: number, v: string) => {
    setMatrix({ ...matrix, [pk]: { ...matrix[pk], prices: { ...matrix[pk].prices, [String(d)]: v } } });
  };
  const setMult = (pk: string, v: string) => {
    setMatrix({ ...matrix, [pk]: { ...matrix[pk], exclusive_multiplier: v } });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/ad-pricing", { placements: matrix, prime });
      toast.success("Ad pricing updated — live on /advertise immediately");
      queryClient.invalidateQueries({ queryKey: ["admin-ad-pricing"] });
      queryClient.invalidateQueries({ queryKey: ["ads-pricing"] });
      queryClient.invalidateQueries({ queryKey: ["ads-traffic-hours"] });
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-ad-pricing-page">
      <PageHeader title="Ad Pricing" sub="Set the price for every placement × flight duration — changes apply instantly to the self-serve /advertise page"
        actions={
          <Button onClick={save} disabled={busy || !matrix} data-testid="ad-pricing-save"
            className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
            <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Save pricing"}
          </Button>
        } />

      {!matrix ? (
        <p className="text-gray-500 font-mono text-sm py-10">Loading pricing…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-[#121212]">
              <tr>
                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">Placement</th>
                {durations.map((d) => (
                  <th key={d} className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-widest text-gray-500">{d} days ($)</th>
                ))}
                <th className="text-left px-3 py-3 font-mono text-[10px] uppercase tracking-widest text-[#F59E0B]">Exclusive ×</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {Object.keys(PLACEMENT_LABELS).map((pk) => (
                <tr key={pk} data-testid={`pricing-row-${pk}`}>
                  <td className="px-4 py-3">
                    <p className="text-white font-medium">{PLACEMENT_LABELS[pk]}</p>
                    <p className="font-mono text-[10px] text-gray-600">{pk}</p>
                  </td>
                  {durations.map((d) => (
                    <td key={d} className="px-3 py-3">
                      <Input type="number" min="1" value={matrix[pk]?.prices?.[String(d)] ?? ""}
                        onChange={(e) => setPrice(pk, d, e.target.value)}
                        data-testid={`price-input-${pk}-${d}`}
                        className="w-24 bg-[#1A1A1A] border-white/10 text-white font-mono text-sm h-9" />
                    </td>
                  ))}
                  <td className="px-3 py-3">
                    <Input type="number" min="1" step="0.5" value={matrix[pk]?.exclusive_multiplier ?? ""}
                      onChange={(e) => setMult(pk, e.target.value)}
                      data-testid={`mult-input-${pk}`}
                      className="w-20 bg-[#1A1A1A] border-[#F59E0B]/30 text-[#F59E0B] font-mono text-sm h-9" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="font-mono text-[11px] text-gray-500 mt-4 leading-relaxed max-w-2xl">
        Standard campaigns rotate in 15-second slots, capped at 480 impressions per ad per day.
        The exclusive multiplier is applied to the base price when an advertiser buys a 24/7 takeover (no rotation, no cap).
      </p>

      {prime && (
        <div className="mt-8 bg-[#121212] border border-[#F59E0B]/30 rounded-xl p-6" data-testid="prime-rate-card">
          <h2 className="flex items-center gap-2 font-heading font-bold text-white text-lg">
            <Zap className="h-4 w-4 text-[#F59E0B]" /> Prime time pricing
          </h2>
          <p className="text-gray-500 text-xs mt-1.5 max-w-2xl leading-relaxed">
            Partial-day bookings (4h / 8h / 12h) pay a premium on hours that fall in your busiest reader hours —
            recalculated automatically from the last 7 days of traffic. Full-day bookings are never surcharged.
            Set the hour count to 0 to disable the premium entirely.
          </p>
          <div className="flex flex-wrap items-end gap-6 mt-4">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Premium (%)</label>
              <Input type="number" min="0" max="300" value={prime.premium_pct ?? ""}
                onChange={(e) => setPrime({ ...prime, premium_pct: e.target.value })}
                data-testid="prime-premium-input"
                className="w-24 bg-[#1A1A1A] border-[#F59E0B]/30 text-[#F59E0B] font-mono text-sm h-9" />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Prime hours (top N busiest)</label>
              <Input type="number" min="0" max="12" value={prime.top_n ?? ""}
                onChange={(e) => setPrime({ ...prime, top_n: e.target.value })}
                data-testid="prime-topn-input"
                className="w-24 bg-[#1A1A1A] border-white/10 text-white font-mono text-sm h-9" />
            </div>
            <div className="min-w-[200px]">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">Current prime hours (ET)</p>
              <div className="flex flex-wrap gap-1.5" data-testid="prime-hours-preview">
                {(data?.prime_hours || []).length === 0 ? (
                  <span className="font-mono text-xs text-gray-600">none — premium inactive</span>
                ) : (data?.prime_hours || []).map((h: number) => (
                  <span key={h} className="font-mono text-[11px] px-2 py-0.5 rounded bg-[#7C3AED]/20 text-[#A78BFA] border border-[#7C3AED]/40">
                    {String(h).padStart(2, "0")}:00
                  </span>
                ))}
              </div>
            </div>
          </div>
          <p className="font-mono text-[10px] text-gray-600 mt-3">
            Preview reflects saved settings — save to recompute. Advertisers see the premium and which of their hours are prime before checkout.
          </p>
        </div>
      )}
    </div>
  );
}
